import classNames from 'clsx';
import { xray } from 'lib/xray';
import debounce from 'lodash.debounce';
import React, { type ReactElement, memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_DEV } from 'reactApp/appHelpers/configHelpers';
import { tariffBuyCustomTitle } from 'reactApp/appHelpers/featuresHelpers';
import { disableAdsModalResizeWait } from 'reactApp/appHelpers/featuresHelpers/features/disableAdsModalResize';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { BuyDisableAds } from 'reactApp/components/BuyDisableAds/BuyDisableAds';
import { BREAKPOINT_SM } from 'reactApp/constants/breakpoints';
import { initPersonalTariffs } from 'reactApp/modules/billingPersonalization/billingPersonalization.module';
import { BillingPersonalizationSelectors } from 'reactApp/modules/billingPersonalization/billingPersonalization.selectors';
import { initProducts } from 'reactApp/modules/products/products.module';
import type { ResizeParams } from 'reactApp/ui/IframePages/BuyDisableAds/BuyDisableAds.types';
import { DisableAdsPostMessageType, sendFrameMessage } from 'reactApp/ui/IframePages/BuyDisableAds/utils';
import { BuyFamilyTariff } from 'reactApp/ui/IframePages/BuyFamilyTariff/BuyFamilyTariff';
import { Loader } from 'reactApp/ui/Loader/Loader';
import { isFramed } from 'reactApp/utils/isFramed';
import opener from 'reactApp/utils/opener';

import styles from './BuyDisableAds.css';
import { useEmbeddedBuyScreen } from './hooks/useEmbeddedBuyScreen';

interface BuyIframeQueryParams {
    title?: string;
    tariffs?: string;
    activeTab?: 'y' | 'm';
    fastcheckoutTitle?: string;
    isFamilyTariff?: boolean;
    analyticsMap?: string;
    usePersonal?: boolean;
    paySource?: string;
}

const {
    title: titleFromQuery,
    tariffs: tariffsFromQuery,
    activeTab: activeTabFromQuery,
    fastcheckoutTitle: fastcheckoutTitleFromQuery,
    isFamilyTariff: isFamilyTariffFromQuery,
    analyticsMap: analyticsFromQuery,
    usePersonal: usePersonalFromQuery,
    paySource: paySourceFromQuery,
}: BuyIframeQueryParams = getQueryParams();

export const BuyDisableAdsIframe = memo((): ReactElement => {
    const dispatch = useDispatch();

    const { isLoaded } = useSelector(BillingPersonalizationSelectors.getLoadingState);

    useEffect(() => {
        dispatch(initProducts());

        if (usePersonalFromQuery) {
            dispatch(initPersonalTariffs());
        }
    }, []);

    if (!isFramed() && !IS_DEV) {
        xray.send(`buy-disable-ads-frame_error_not_frame`);
        opener('/', true);
    }

    const fastcheckoutTitle = isFamilyTariffFromQuery
        ? 'Семейная подписка Mail Space'
        : fastcheckoutTitleFromQuery
        ? tariffBuyCustomTitle[fastcheckoutTitleFromQuery]
        : undefined;

    const onResize = useCallback(
        debounce(({ height }: ResizeParams) => {
            sendFrameMessage({ type: DisableAdsPostMessageType.size, height, width: BREAKPOINT_SM });
        }, disableAdsModalResizeWait),
        []
    );

    const { embeddedContent, openEmbeddedBuy } = useEmbeddedBuyScreen(fastcheckoutTitle, analyticsFromQuery, tariffsFromQuery, onResize);

    if (!isLoaded && usePersonalFromQuery) {
        return <Loader key="loader" isModal={true} />;
    }

    if (isFamilyTariffFromQuery) {
        return (
            <div
                className={classNames(styles.root, {
                    [styles.familyRoot]: isFamilyTariffFromQuery,
                })}
            >
                <BuyFamilyTariff onBuyClick={openEmbeddedBuy} />
                {embeddedContent}
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <BuyDisableAds
                titleFromQuery={titleFromQuery}
                tariffsNumber={tariffsFromQuery}
                activeTabFromQuery={activeTabFromQuery}
                onBuyClick={openEmbeddedBuy}
                analyticsFromQuery={analyticsFromQuery}
                onResize={onResize}
                usePersonal={usePersonalFromQuery}
                paySource={paySourceFromQuery}
                isIframe
            />
            {embeddedContent}
        </div>
    );
});

BuyDisableAdsIframe.displayName = 'BuyDisableAdsIframe';
